/**
 * API keys — E15 slice 4 (docs/fibtec-etlgen-e15-design-spec.md §4). An organisation's Owner
 * creates, lists and revokes the long-lived keys a program (a CI job, a scheduled task) uses
 * instead of a signed-in person's one-hour token.
 *
 * A new key is shown exactly once, here, straight from the create response: the server stores only
 * a hash and cannot show it again. It lives in this component's state and nowhere else (not in
 * storage, the URL or the console) and is dropped when the person dismisses it or leaves the page.
 * While it is on screen the form is disabled, so a second key cannot replace it before it is saved.
 *
 * `generate` is never pre-selected: it is the scope that spends credit. Expiry is optional, with a
 * nudge: 90 days is the default and "No expiry" says what it means.
 */

import { useCallback, useEffect, useState } from 'react';

import {
  ApiError,
  createApiKey,
  listApiKeys,
  revokeApiKey,
  type ApiKey,
  type ApiKeyScope,
  type CreatedApiKey,
} from '../api/client';
import { KeyApiAddress } from '../components/KeyApiAddress';
import { config } from '../config';
import {
  DEFAULT_EXPIRY_ID,
  EXPIRY_CHOICES,
  MAX_LABEL_LENGTH,
  expiryTimestamp,
  keyStatus,
} from '../lib/apiKeys';

function formatWhen(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatDay(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

const SCOPE_LABEL: Record<ApiKeyScope, string> = { read: 'Read', generate: 'Generate' };

function apiMessage(err: unknown, fallback: string): string {
  return err instanceof ApiError ? err.message : fallback;
}

export function ApiKeys() {
  const [keys, setKeys] = useState<ApiKey[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [ownerOnly, setOwnerOnly] = useState(false);

  const [label, setLabel] = useState('');
  const [scopeRead, setScopeRead] = useState(true);
  const [scopeGenerate, setScopeGenerate] = useState(false);
  const [expiryId, setExpiryId] = useState(DEFAULT_EXPIRY_ID);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');

  const [created, setCreated] = useState<CreatedApiKey | null>(null);
  const [copyNote, setCopyNote] = useState('');

  const [confirming, setConfirming] = useState<string | null>(null);
  const [revoking, setRevoking] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError('');
    try {
      setKeys((await listApiKeys()).api_keys);
      setOwnerOnly(false);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setOwnerOnly(true);
      } else {
        setError(apiMessage(err, 'Could not load API keys.'));
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const expiry = EXPIRY_CHOICES.find((choice) => choice.id === expiryId) ?? EXPIRY_CHOICES[0]!;

  async function create(event: React.FormEvent) {
    event.preventDefault();
    const trimmed = label.trim();
    const scopes: ApiKeyScope[] = [
      ...(scopeRead ? (['read'] as const) : []),
      ...(scopeGenerate ? (['generate'] as const) : []),
    ];
    if (!trimmed || trimmed.length > MAX_LABEL_LENGTH) {
      setCreateError(`Give the key a label of 1 to ${MAX_LABEL_LENGTH} characters.`);
      return;
    }
    if (scopes.length === 0) {
      setCreateError('Choose at least one access level.');
      return;
    }

    setCreating(true);
    setCreateError('');
    try {
      const result = await createApiKey({
        label: trimmed,
        scopes,
        expiresAt: expiryTimestamp(expiry.days, new Date()),
      });
      setCreated(result);
      setCopyNote('');
      setLabel('');
      await load();
    } catch (err) {
      setCreateError(apiMessage(err, 'Could not create the key.'));
    } finally {
      setCreating(false);
    }
  }

  async function copyKey() {
    if (!created) return;
    try {
      await navigator.clipboard.writeText(created.api_key);
      setCopyNote('Copied to the clipboard.');
    } catch {
      setCopyNote('Could not copy automatically. Select the key and copy it yourself.');
    }
  }

  async function revoke(keyId: string) {
    setRevoking(keyId);
    setError('');
    try {
      await revokeApiKey(keyId);
      setConfirming(null);
      await load();
    } catch (err) {
      setError(apiMessage(err, 'Could not revoke the key.'));
    } finally {
      setRevoking(null);
    }
  }

  const now = new Date();

  return (
    <main className="page page--wide">
      <h1>API keys</h1>
      <p className="muted">
        A key lets a program call the ETL Integrator without a signed-in person: a CI job, a
        scheduled task, a script. It acts as your organisation, and a key with the Generate
        access spends your credit (or your free-plan allowance). Keep keys secret, give each its
        own label, prefer an expiry, and revoke any you no longer use.
      </p>
      {/* A static page of its own (scripts/build-api-docs.mjs), so a plain link, not a route. */}
      <p className="muted">
        The <a href="/api-docs.html">API reference</a> lists every route, both ways to sign in and
        every error.
      </p>

      <KeyApiAddress address={config.keyApiUrl} />

      {error && (
        <p className="alert alert--error" role="alert">
          {error}
        </p>
      )}

      {loading ? (
        <p className="muted">Loading…</p>
      ) : ownerOnly ? (
        <p className="alert alert--info">
          Only an organisation owner can manage API keys. Ask your owner to create one.
        </p>
      ) : (
        <>
          {created && (
            <div className="result" role="status" aria-live="polite">
              <h2>Your new key</h2>
              <p>
                <strong>Copy it now.</strong> It is not stored anywhere we can show it again; if
                you lose it, revoke it and create another.
              </p>
              <input
                className="mono"
                type="text"
                readOnly
                value={created.api_key}
                aria-label="New API key"
                autoComplete="off"
                spellCheck={false}
                onFocus={(event) => event.currentTarget.select()}
              />
              <div className="field-inline">
                <button className="btn btn--primary" onClick={() => void copyKey()}>
                  Copy key
                </button>
                <button className="btn btn--ghost" onClick={() => setCreated(null)}>
                  I have saved it
                </button>
              </div>
              {copyNote && <p className="muted small">{copyNote}</p>}
              <p className="muted small">
                Send it as <span className="mono">Authorization: Bearer &lt;key&gt;</span> to the
                key API, or set <span className="mono">ETLGEN_API_KEY</span> for the command-line
                client. Never put it in code, a repository or a chat.
              </p>
            </div>
          )}

          <form className="result" onSubmit={(event) => void create(event)}>
            <h2>Create a key</h2>
            {created && (
              <p className="muted small">Save or dismiss the new key above before creating another.</p>
            )}
            <div className="field">
              <label className="field-label" htmlFor="api-key-label">
                Label
              </label>
              <input
                id="api-key-label"
                type="text"
                value={label}
                maxLength={MAX_LABEL_LENGTH}
                onChange={(event) => setLabel(event.target.value)}
                placeholder="e.g. nightly CI"
                aria-describedby="api-key-label-hint"
                disabled={creating || created !== null}
              />
              <span id="api-key-label-hint" className="field-hint">
                So you can tell the keys apart and revoke the right one.
              </span>
            </div>

            <fieldset className="field" disabled={creating || created !== null}>
              <legend className="field-label">Access</legend>
              <label>
                <input
                  type="checkbox"
                  checked={scopeRead}
                  onChange={(event) => setScopeRead(event.target.checked)}
                />{' '}
                Read: price estimates, the activity summary and the balance.
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={scopeGenerate}
                  onChange={(event) => setScopeGenerate(event.target.checked)}
                />{' '}
                Generate: create ETL scripts, connectors and webhook handlers. This spends
                credit.
              </label>
            </fieldset>

            <div className="field">
              <label className="field-label" htmlFor="api-key-expiry">
                Expires
              </label>
              <select
                id="api-key-expiry"
                value={expiryId}
                onChange={(event) => setExpiryId(event.target.value)}
                aria-describedby={expiry.days === null ? 'api-key-expiry-hint' : undefined}
                disabled={creating || created !== null}
              >
                {EXPIRY_CHOICES.map((choice) => (
                  <option key={choice.id} value={choice.id}>
                    {choice.label}
                  </option>
                ))}
              </select>
              {expiry.days === null && (
                <span id="api-key-expiry-hint" className="field-hint">
                  A key with no expiry works until you revoke it. If it leaks, it keeps working.
                </span>
              )}
            </div>

            {createError && (
              <p className="alert alert--error" role="alert">
                {createError}
              </p>
            )}

            <button className="btn btn--primary" type="submit" disabled={creating || created !== null}>
              {creating ? 'Creating…' : 'Create key'}
            </button>
          </form>

          <div className="result">
            <h2>Your keys</h2>
            {keys && keys.length === 0 ? (
              <p className="muted small">No API keys yet.</p>
            ) : (
              <table className="schema-review-table">
                <thead>
                  <tr>
                    <th>Label</th>
                    <th>Access</th>
                    <th>Created</th>
                    <th>Expires</th>
                    <th>Last used</th>
                    <th>Status</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {(keys ?? []).map((key) => {
                    const status = keyStatus(key, now);
                    return (
                      <tr key={key.key_id}>
                        <td>{key.label}</td>
                        <td>{key.scopes.map((scope) => SCOPE_LABEL[scope]).join(', ')}</td>
                        <td>
                          {formatWhen(key.created_at)}
                          <div className="muted small">{key.created_by}</div>
                        </td>
                        <td className="muted">
                          {key.expires_at ? formatDay(key.expires_at) : 'Never'}
                        </td>
                        <td className="muted">
                          {key.last_used_at ? formatWhen(key.last_used_at) : 'Never'}
                        </td>
                        <td>
                          {status === 'active' && 'Active'}
                          {status === 'expired' && 'Expired'}
                          {status === 'revoked' && (
                            <>
                              Revoked
                              <div className="muted small">
                                {key.revoked_at ? formatDay(key.revoked_at) : ''}
                                {key.revoked_by ? ` by ${key.revoked_by}` : ''}
                              </div>
                            </>
                          )}
                        </td>
                        <td>
                          {status === 'active' &&
                            (confirming === key.key_id ? (
                              <>
                                <button
                                  className="btn btn--primary"
                                  disabled={revoking === key.key_id}
                                  onClick={() => void revoke(key.key_id)}
                                  aria-label={`Confirm revoking ${key.label}`}
                                >
                                  {revoking === key.key_id ? 'Revoking…' : 'Revoke now'}
                                </button>{' '}
                                <button
                                  className="btn btn--ghost"
                                  disabled={revoking === key.key_id}
                                  onClick={() => setConfirming(null)}
                                >
                                  Cancel
                                </button>
                              </>
                            ) : (
                              <button
                                className="btn btn--ghost"
                                onClick={() => setConfirming(key.key_id)}
                                aria-label={`Revoke ${key.label}`}
                              >
                                Revoke
                              </button>
                            ))}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
            <p className="muted small">
              Revoking stops a key at once: its next call is refused. Revoked keys stay listed as
              a record of who created and revoked them.
            </p>
          </div>
        </>
      )}
    </main>
  );
}
